import 'vite/modulepreload-polyfill';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import { createApp, onMounted, onUnmounted, ref } from 'vue';

import { ANIMATION_CONFIGS } from '~/constants/animations';

import { BaseSection } from './base-section';

const SECTION_NAME = 'stories-carousel';
import * as AnalyticsData from '../analytics/analytics-data';

/**
 * Stories Carousel Section
 *
 * This section displays a slider of stories with navigation controls.
 * The section uses Vue.js with Composition API and Swiper.js for the slider functionality.
 *
 * @extends BaseSection
 */
class StoriesCarousel extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const sectionInstance = this;

    createApp({
      name: 'StoriesCarouselSection',
      setup() {
        const carousel = ref(null);

        onMounted(() => {
          const swiperContainer = container.querySelector('.swiper');

          if (swiperContainer) {
            carousel.value = new Swiper(swiperContainer, {
              breakpoints: {
                768: {
                  centeredSlides: true,
                  slidesPerView: 3.5,
                },
              },
              keyboard: {
                enabled: true,
              },
              loop: true,
              modules: [Navigation, Pagination, Keyboard],
              navigation: {
                nextEl: '.swiper-next-button',
                prevEl: '.swiper-prev-button',
              },
              slidesOffsetAfter: 10,
              slidesOffsetBefore: 10,
              slidesPerView: 1.2,
              spaceBetween: 10,
            });
          }

          // Hook analytics event to CTA links
          if (sectionInstance.isHomePageTemplate()) {
            AnalyticsData.hookHomepageModuleClicked({
              sectionName: SECTION_NAME,
            });
          }
        });

        onUnmounted(() => {
          if (carousel.value) {
            carousel.value.destroy();
            carousel.value = null;
          }
        });

        return {};
      },
    }).mount(container);
  }

  /**
   * Sets up a ScrollTrigger animation instead of using IntersectionObserver
   * This is used to initialize the animation with ScrollTrigger instead of the default behavior
   *
   * @param {Object} instanceData - The section instance data
   */
  setupInViewAnimation(instanceData) {
    if (instanceData.animation) {
      instanceData.animation.paused(false);

      ScrollTrigger.refresh();
    } else {
      const animation = this.setupAnimation(instanceData, () =>
        this.setupInViewAnimationTimeline(instanceData),
      );

      if (animation && animation.scrollTrigger) {
        instanceData.scrollTrigger = animation.scrollTrigger;
      }
    }
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    if (!container) return null;

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) return null;
    tl.fromTo(container, { opacity: 0 }, ANIMATION_CONFIGS.FADE_IN, 0);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new StoriesCarousel();
});
